import type { LoggerDraft } from './loggerDraft';
import type { LoggerMode } from '../loggerMode';
import { hydrateDraft } from './hydrateDraft';

// Pure validation/derivation for the persisted in-progress-workout-draft
// blob. Platform-neutral on purpose (RW-18 split): the actual AsyncStorage
// vs localStorage read/write lives in ./loggerDraftStorage(.web).ts, which
// imports parsePersistedBlob/hasCommittedWork from here. Keeping the
// validation logic in ONE file (not duplicated per platform) matters more
// here than almost anywhere else in the logger — this is exactly the code
// class behind the 2026-06-30 toxic-draft incidents, and a second copy that
// could drift is the bug those incidents are about.
//
// Same key namespace as web ("jl.workoutLog.v1") for parity — see
// loggerDraftStorage.ts. The blob stores both the draft and the modal
// "open" flag so the parent can re-open the Logger on next mount when
// there was a live session in progress at backgrounding time.

export type PersistedLoggerState = {
  draft: LoggerDraft;
  open: boolean;
  /** Crash-recovery boot marker (T1, 1.47.0). True iff the draft has
   *  any set whose pill is past pending (completedAt !== null) at the
   *  time of the last write. The cold-boot path at WorkoutsListScreen
   *  reads this to distinguish "real in-progress workout that needs
   *  explicit Resume/Discard" from "stale empty draft" — only the
   *  former prompts. Missing on pre-1.47 persisted blobs (treated as
   *  false). */
  inProgress: boolean;
  lastWrittenAt: string;
};

const isPersistedLoggerState = (v: unknown): v is PersistedLoggerState => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  if (typeof o['open'] !== 'boolean') return false;
  if (typeof o['lastWrittenAt'] !== 'string') return false;
  // inProgress missing on legacy blobs — accept and default at read time.
  if (
    o['inProgress'] !== undefined &&
    typeof o['inProgress'] !== 'boolean'
  )
    return false;
  const d = o['draft'];
  if (typeof d !== 'object' || d === null) return false;
  const draft = d as Record<string, unknown>;
  if (typeof draft['startedAt'] !== 'string') return false;
  if (!Array.isArray(draft['entries'])) return false;
  return true;
};

/** Derived from the draft itself so callers don't have to remember to
 *  pass the right flag; mirrors SL's "live" semantics — any set with
 *  completedAt set counts as committed work. */
export const hasCommittedWork = (draft: LoggerDraft): boolean => {
  for (const e of draft.entries) {
    for (const s of e.sets) {
      if (s.completedAt !== null) return true;
    }
  }
  return false;
};

/** Whether the logger's draft should be mirrored to durable storage.
 *  - `'log'` → YES. Covers both a new/plan workout and a REPEAT: a repeat
 *    mints a BRAND-NEW unsaved session, it is NOT server-backed, so it must
 *    persist like a new workout or the only copy (the in-memory reducer) is
 *    lost when the athlete navigator unmounts on an experience switch — the
 *    LG-10b data-loss (2026-07-09).
 *  - `'log-past'` → NO: a true edit is server-backed; the source of truth is
 *    the session on the server, so it must not compete for the single
 *    new-workout draft slot.
 *  - `'prescribe'` → NO: a prescription is not an in-progress workout and has
 *    no business holding the draft slot at all.
 *  ⇒ the predicate is exactly `mode === 'log'`. It used to re-derive the same
 *  bit from the editing target as a De Morgan negation
 *  (`!editing || editing.mode === 'repeat'`), which is why it reads inverted
 *  in git history. Takes the mode, not the EditingTarget, so it stays a pure,
 *  unit-testable predicate (PE-22 slice A). */
export const shouldPersistDraft = (mode: LoggerMode): boolean =>
  mode === 'log';

export const isNonTrivialDraft = (draft: LoggerDraft): boolean => {
  if (draft.entries.length === 0) return false;
  for (const e of draft.entries) {
    for (const s of e.sets) {
      // Defensive `?? ''`: a persisted set written by an older app
      // version may omit a now-required text field (Leonardo's
      // post-update toxic draft 2026-06-30 had no `note`). loadPersisted
      // normalises via hydrateDraft, but this predicate is also called
      // on raw drafts (useBootDraftRecovery), so it must never assume
      // every key is present — a missing key crashed the boot path
      // (same class as 9350821c).
      if ((s.reps ?? '').trim() !== '' || (s.weight ?? '').trim() !== '')
        return true;
      if (s.completedAt != null) return true;
      // rir included (AP-0a review): on effortScale='rir' the input
      // clears the rpe field, so an RIR-only set was invisible to this
      // predicate and a killed session with just an RIR typed risked
      // being discarded as a trivial draft — a landmine once the effort
      // meter (AP-0b) makes RIR-first entry the default gesture.
      if (
        (s.note ?? '').trim() !== '' ||
        (s.rpe ?? '').trim() !== '' ||
        (s.rir ?? '').trim() !== ''
      )
        return true;
    }
  }
  // Even an empty-set entry counts: the user explicitly picked an
  // exercise and we don't want them to lose that selection on a
  // background/kill cycle.
  return draft.entries.length > 0;
};

/** Parse + validate + NORMALISE a persisted-state JSON blob. The single
 *  chokepoint that brings any persisted draft (live slot OR a quarantine
 *  slot) to the current shape before a consumer reads it: a blob written
 *  by an older app version can omit now-required fields (Leonardo's
 *  2026-06-30 toxic draft had sets with no `note`); hydrateDraft backfills
 *  every key so the logger / cold-boot recovery / save path never crash on
 *  `undefined.trim()` (the boot-loop → toxic-quarantine root cause).
 *  Returns null on unparseable / shape-invalid input. */
export function parsePersistedBlob(raw: string): PersistedLoggerState | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPersistedLoggerState(parsed)) return null;
    return {
      ...parsed,
      // ⭐ The ONLY place the legacy effort-seed migration is allowed to run.
      // This is the AsyncStorage blob: a pending set here can only be
      // carrying a seeded prescription, because the build that wrote it put
      // one there. The reducer's own hydrateDraft call (loggerNestedReducer
      // 'hydrate') deliberately does NOT pass the flag — it also serves the
      // EDIT workflow, where a legacy saved set with no completedAt holds a
      // REAL measurement, and migrating that would move it out of the field
      // the save path reads and drop it from the athlete's history.
      draft: hydrateDraft(parsed.draft, { migrateLegacyEffortSeed: true }),
      // Backfill legacy blobs lacking inProgress so callers can rely on
      // a boolean. Pre-1.47 → treat as "not in progress".
      inProgress: parsed.inProgress ?? false,
    };
  } catch {
    return null;
  }
}
